import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export default function Checkout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get('plan');

  const [plan, setPlan] = useState(null);
  const [phone, setPhone] = useState('260');
  const [network, setNetwork] = useState('airtel');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const [modal, setModal] = useState('hidden');
  const [statusText, setStatusText] = useState('Initializing payment…');
  const [progress, setProgress] = useState(0);
  const [attemptText, setAttemptText] = useState('');
  const [timerText, setTimerText] = useState('');
  const [successMsg, setSuccessMsg] = useState('Credits have been added to your account');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!planId) {
      nav('/pricing');
      return;
    }
    (async () => {
      try {
        const res = await api.get('/api/pricing/plans');
        const match = res.plans.find((p) => p.id === planId);
        if (!match) {
          setErr('Plan not found');
          return;
        }
        setPlan(match);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load plan');
      }
    })();
  }, [planId, nav]);

  async function submit(e) {
    e.preventDefault();
    if (!plan) return;
    if (!/^260[0-9]{9}$/.test(phone)) {
      setErr('Enter a valid Zambian phone number starting with 260');
      return;
    }
    setErr(null);
    setBusy(true);
    setModal('processing');
    setStatusText('Initializing payment…');
    setProgress(0);
    setAttemptText('');
    const startTime = Date.now();
    const timerHandle = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const m = Math.floor(elapsed / 60);
      const s = (elapsed % 60).toString().padStart(2, '0');
      setTimerText(`(${m}:${s})`);
    }, 1000);

    try {
      const res = await api.post('/api/payment/initiate', {
        plan_id: plan.id,
        phone_number: phone.trim(),
        network,
      });
      if (!res.success) throw new Error(res.message || 'Payment initiation failed');
      setStatusText('Payment initiated — check your phone');
      setProgress(5);
      await pollVerify(res.deposit_id, startTime);
    } catch (e) {
      showFailure(e instanceof Error ? e.message : 'Payment failed');
    } finally {
      clearInterval(timerHandle);
      setBusy(false);
    }
  }

  async function pollVerify(depositId, startTime) {
    const maxMs = 2 * 60 * 1000;
    let attempt = 0;
    const nextInterval = (a) => (a <= 3 ? 2000 : a <= 8 ? 3000 : a <= 18 ? 5000 : 8000);

    while (true) {
      attempt++;
      const elapsed = Date.now() - startTime;
      if (elapsed > maxMs) {
        showFailure(
          'Payment not confirmed after 2 minutes. If your money was deducted, credits will be added shortly.',
        );
        return;
      }
      setProgress(Math.min(98, (elapsed / maxMs) * 100));
      const nextSec = nextInterval(attempt) / 1000;
      setAttemptText(`Attempt ${attempt} • Next check in ${nextSec}s`);

      try {
        const res = await api.get(`/api/payment/verify/${depositId}`);
        const s = (res.status || '').toUpperCase();
        if (s === 'COMPLETED' || s === 'SUCCESS') {
          setProgress(100);
          setSuccessMsg(
            res.credits_added
              ? `${res.credits_added} credits have been added to your account!`
              : res.message || 'Credits have been added to your account',
          );
          setModal('success');
          return;
        }
        if (['FAILED', 'REJECTED', 'CANCELLED', 'EXPIRED', 'DECLINED'].includes(s)) {
          showFailure(res.message || 'Payment was not completed');
          return;
        }
        setStatusText(res.message || 'Checking payment status…');
      } catch {
        setStatusText('Connection issue, retrying…');
      }
      await new Promise((r) => setTimeout(r, nextInterval(attempt)));
    }
  }

  function showFailure(msg) {
    setErrorMsg(msg);
    setModal('failed');
  }

  function closeModal() {
    setModal('hidden');
  }

  if (!user) {
    return (
      <div className="main-container">
        <div className="card mx-auto" style={{ maxWidth: 420 }}>
          <div className="card-body text-center">
            <p className="mb-3">You need to sign in to buy credits.</p>
            <Link to={`/login?next=${encodeURIComponent(`/checkout?plan=${planId ?? ''}`)}`} className="btn btn-cta">
              <i className="fas fa-sign-in-alt me-1"></i>Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container">
      <div className="row justify-content-center">
        <div className="col-lg-6 col-md-8">
          {plan && (
            <div className="card mb-4">
              <div
                style={{
                  background: 'linear-gradient(135deg, var(--primary), var(--secondary))',
                  color: '#fff',
                  padding: '1.1rem 1.4rem',
                  borderRadius: '12px 12px 0 0',
                }}
              >
                <h4 className="mb-0">
                  <i className="fas fa-shopping-cart me-2"></i>Checkout
                </h4>
              </div>
              <div className="card-body">
                <h5 className="text-center mb-3">{plan.name}</h5>
                <div className="text-center mb-4">
                  <div style={{ fontSize: 42, fontWeight: 800, color: 'var(--primary)' }}>
                    K{plan.price_zmw}
                  </div>
                  <div className="text-muted">
                    {plan.credits} credit{plan.credits > 1 ? 's' : ''}
                  </div>
                  <small className="text-muted">{plan.description}</small>
                </div>
                <div className="alert alert-info mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Payment:</strong> Mobile Money (Airtel, MTN, Zamtel)
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body p-4">
              <h5 className="mb-4">Payment details</h5>
              {err && <div className="alert alert-danger">{err}</div>}

              <form onSubmit={submit}>
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-phone me-2"></i>Phone number
                  </label>
                  <input
                    type="tel"
                    className="form-control form-control-lg"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="260XXXXXXXXX"
                    pattern="260[0-9]{9}"
                    disabled={busy}
                    required
                  />
                  <div className="form-text">Zambian number starting with 260.</div>
                </div>

                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="fas fa-network-wired me-2"></i>Mobile money provider
                  </label>
                  <div className="row g-3">
                    <NetworkOption
                      id="airtel"
                      label="Airtel"
                      selected={network === 'airtel'}
                      color="danger"
                      onSelect={() => setNetwork('airtel')}
                      disabled={busy}
                    />
                    <NetworkOption
                      id="mtn"
                      label="MTN"
                      selected={network === 'mtn'}
                      color="warning"
                      onSelect={() => setNetwork('mtn')}
                      disabled={busy}
                    />
                    <NetworkOption
                      id="zamtel"
                      label="Zamtel"
                      selected={network === 'zamtel'}
                      color="success"
                      onSelect={() => setNetwork('zamtel')}
                      disabled={busy}
                    />
                  </div>
                </div>

                <button type="submit" className="btn btn-cta btn-lg w-100 mb-3" disabled={busy || !plan}>
                  <i className="fas fa-lock me-2"></i>
                  {plan ? `Pay K${plan.price_zmw} Securely` : 'Loading…'}
                </button>
                <Link to="/pricing" className="btn btn-outline-secondary w-100">
                  <i className="fas fa-arrow-left me-2"></i>Back to pricing
                </Link>
              </form>
            </div>
          </div>
        </div>
      </div>

      {modal !== 'hidden' && (
        <PaymentModal
          state={modal}
          statusText={statusText}
          timerText={timerText}
          attemptText={attemptText}
          progress={progress}
          successMsg={successMsg}
          errorMsg={errorMsg}
          onDismiss={closeModal}
          onGoDashboard={() => nav('/dashboard')}
        />
      )}
    </div>
  );
}

function NetworkOption({ id, label, selected, color, onSelect, disabled }) {
  const classes = selected
    ? `btn btn-${color} w-100 d-flex flex-column align-items-center py-3`
    : `btn btn-outline-${color} w-100 d-flex flex-column align-items-center py-3`;
  return (
    <div className="col-4">
      <button type="button" className={classes} onClick={onSelect} disabled={disabled} id={id}>
        <i className="fas fa-mobile-alt fa-2x mb-2"></i>
        <span>{label}</span>
      </button>
    </div>
  );
}

function PaymentModal({
  state,
  statusText,
  timerText,
  attemptText,
  progress,
  successMsg,
  errorMsg,
  onDismiss,
  onGoDashboard,
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.55)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 1050,
      }}
    >
      <div
        className="card"
        style={{ width: 'min(92vw, 460px)', borderRadius: 14, overflow: 'hidden' }}
      >
        <div className="card-body text-center p-5">
          {state === 'processing' && (
            <>
              <div
                className="spinner-border text-primary mb-3"
                style={{ width: '3rem', height: '3rem' }}
                role="status"
              >
                <span className="visually-hidden">Loading…</span>
              </div>
              <h5 className="mb-3">Processing payment…</h5>
              <p className="text-muted">Approve the payment on your phone.</p>
              <div className="alert alert-info mt-3">
                <i className="fas fa-mobile-alt me-2"></i>
                Check your phone for a payment prompt.
              </div>
              <div className="mt-4">
                <div className="progress" style={{ height: 8 }}>
                  <div
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div className="mt-2">
                  <small className="text-muted">
                    {statusText} <span className="ms-2">{timerText}</span>
                  </small>
                </div>
                {attemptText && (
                  <div className="mt-1">
                    <small className="text-muted">{attemptText}</small>
                  </div>
                )}
              </div>
            </>
          )}
          {state === 'success' && (
            <>
              <i
                className="fas fa-check-circle text-success mb-3"
                style={{ fontSize: '4rem' }}
              ></i>
              <h5 className="mb-3">Payment successful!</h5>
              <p className="text-muted">{successMsg}</p>
              <button type="button" className="btn btn-cta mt-3" onClick={onGoDashboard}>
                Go to dashboard
              </button>
            </>
          )}
          {state === 'failed' && (
            <>
              <i
                className="fas fa-times-circle text-danger mb-3"
                style={{ fontSize: '4rem' }}
              ></i>
              <h5 className="mb-3">Payment failed</h5>
              <p className="text-muted">{errorMsg}</p>
              <button type="button" className="btn btn-outline-secondary mt-3" onClick={onDismiss}>
                Try again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
